import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { Link, useNavigate, useLocation } from 'react-router-dom'; // Remove router imports
import { getCategories } from '../../utils/dataService';
import { FaSearch, FaTimes, FaBars } from 'react-icons/fa';
import { debounce } from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';
import { getCategoryHeaderClass, getCategorySlug } from '../../utils/helpers';

// Responsive breakpoints for consistent layout
const BREAKPOINTS = {
  xs: 480,  // Extra small devices
  sm: 640,  // Mobile landscape
  md: 768,  // Tablet portrait
  lg: 1024, // Tablet landscape/small laptop
  xl: 1280, // Large screens
  xxl: 1536 // Extra large screens
};

// Safely get window width to prevent errors on iOS
const getWindowWidth = () => {
  try {
    if (typeof window === 'undefined') return 0;
    return window.innerWidth || 0;
  } catch (error) {
    console.error("Error getting window width:", error);
    return 800; // Default value to prevent app crash
  }
};

// Function to get category short name for nav links
const getCategoryShortName = (categoryName) => {
  const nameMap = {
    'Hidden Destinations': 'Hidden',
    'Adventure Escapes': 'Adventure',
    'Cultural Experiences': 'Cultural',
    'Travel Tips': 'Tips'
  };
  return nameMap[categoryName] || categoryName;
};

// Get category-specific color class for styling
const getCategoryColorClass = (categorySlug) => {
  if (!categorySlug) return 'text-gray-200 hover:text-white';
  
  const categoryName = categorySlug.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  switch (categoryName) {
    case 'Hidden Destinations':
      return 'text-purple-300 hover:text-purple-100';
    case 'Adventure Escapes':
      return 'text-red-300 hover:text-red-100';
    case 'Cultural Experiences':
      return 'text-blue-300 hover:text-blue-100';
    case 'Travel Tips':
      return 'text-green-300 hover:text-green-100';
    default:
      return 'text-gray-200 hover:text-white';
  }
};

// Custom close button component for better styling
const CloseButton = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className="w-5 h-5"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([
    // Static categories as fallback to prevent disappearing during load
    { id: 1, name: 'Hidden Destinations', slug: 'hidden-destinations' },
    { id: 2, name: 'Adventure Escapes', slug: 'adventure-escapes' },
    { id: 3, name: 'Cultural Experiences', slug: 'cultural-experiences' },
    { id: 4, name: 'Travel Tips', slug: 'travel-tips' }
  ]);
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const [searchInputVisible, setSearchInputVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState('');
  // const location = useLocation(); // Remove useLocation
  const searchInputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchContainerRef = useRef(null);

  // const navigate = useNavigate(); // Remove useNavigate
  
  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await getCategories();
        // Only update if we actually got categories from the server
        if (fetchedCategories && fetchedCategories.length > 0) {
          setCategories(fetchedCategories);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        // Keep the static categories as fallback
      }
    };
    loadCategories();
  }, []);

  // Update window width
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(getWindowWidth());
      // Auto-close mobile menu on larger screens
      if (getWindowWidth() >= BREAKPOINTS.md) {
        setMobileMenuOpen(false);
        // Auto-hide search on resize if visible on mobile
        if (searchInputVisible && getWindowWidth() < BREAKPOINTS.md) {
          setSearchInputVisible(false);
        }
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [searchInputVisible]);

  // Close search input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current && 
        !searchContainerRef.current.contains(event.target) && 
        searchButtonRef.current && 
        !searchButtonRef.current.contains(event.target) && 
        searchInputVisible &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setSearchInputVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchInputVisible]);

  // Focus search input when visible
  useEffect(() => {
    if (searchInputVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchInputVisible]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    // Close search if open when toggling menu
    if (searchInputVisible) {
      setSearchInputVisible(false);
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Toggle search input
  const toggleSearchInput = () => {
    // Close menu if open when toggling search
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    setSearchInputVisible(!searchInputVisible);
  };

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results page with the query
      window.location.href = `/search.html?q=${encodeURIComponent(searchQuery.trim())}`; // Use search.html
    }
  };

  // Determine how many categories to show based on screen width
  const getVisibleCategoriesCount = () => {
    if (windowWidth >= BREAKPOINTS.xxl) return 4;  // Only show all on very large screens
    if (windowWidth >= BREAKPOINTS.xl) return 3;   // 3 categories on large screens
    if (windowWidth >= 1150) return 2;             // 2 categories on medium-large screens  
    return 0; // On smaller screens, all categories are in the menu
  };

  // Determine if hamburger menu should be shown
  const shouldShowHamburgerMenu = () => {
    // Show hamburger menu when categories are hidden OR on mobile/tablet
    return getVisibleCategoriesCount() < 4 || windowWidth < BREAKPOINTS.xl;
  };
  
  // Helper to check if a link is active (based on window.location)
  const isNavLinkActive = (path) => {
    // Basic check, might need refinement for query params etc.
    // For category links, check base path and category query param
    if (path.startsWith('/category.html?category=')) {
      const currentPath = window.location.pathname + window.location.search;
      return currentPath === path;
    }
    // Check base path for other links
    return window.location.pathname === path.replace('.html', '') || window.location.pathname + '.html' === path;
  };

  useEffect(() => {
    // 检测当前页面
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const category = params.get('category');
    
    if (path.includes('/category') && category) {
      setCurrentPage(category);
    } else {
      setCurrentPage('');
    }
  }, []);

  const getNavLinkClass = (categorySlug) => {
    const isActive = currentPage === categorySlug;
    if (isActive) {
      // 如果是当前页面，使用slate主题色，悬停时保持同样的slate色
      return 'px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 bg-slate-600 text-white hover:bg-slate-600 hover:text-white';
    } else {
      // 否则保持白色，悬停时显示slate色
      return 'px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 text-white hover:text-white hover:bg-slate-600';
    }
  };

  return (
    <header className="bg-gradient-to-r from-stone-700 to-stone-800 text-white shadow-lg sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-6">
        <div className="flex items-center justify-between h-14 md:h-16 relative">
          {/* Site Title with Logo */}
          <div className={`flex items-center flex-shrink-0 transition-opacity duration-300 ${searchInputVisible && windowWidth < BREAKPOINTS.md ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <a href="/index.html" className="flex items-center group">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-10 w-10 mr-2">
                  <img src="/assets/images/travelradar-38-38.svg" alt="TravelRadar Logo" className="h-9 w-9 object-contain" />
                </div>
                <div className="flex flex-col justify-center h-10">
                  <span className="text-lg md:text-xl font-bold text-white transition-all duration-300 group-hover:text-slate-300 leading-tight">TravelRadar</span>
                  <span className={`text-[10px] md:text-xs text-stone-300 leading-tight -mt-0.5 ${windowWidth < 400 ? 'hidden' : 'block'}`}>Discover Amazing Travel Destinations & Experiences</span>
                </div>
              </div>
            </a>
          </div>

          {/* Main Navigation Area & Search */}
          <div className={`flex items-center ${windowWidth >= BREAKPOINTS.md ? 'flex-grow justify-end' : 'justify-end'}`}>
            <nav className={`hidden ${windowWidth >= 1150 ? 'md:flex' : 'hidden'} items-center space-x-1 sm:space-x-2 lg:space-x-3 mr-2 transition-all duration-300 ease-in-out ${searchInputVisible && windowWidth >= BREAKPOINTS.md ? 'opacity-50 -translate-x-8 pointer-events-none flex-shrink-0' : 'opacity-100 translate-x-0 flex-shrink-0'}`}>
              <a 
                href="/index.html" 
                className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs sm:text-sm font-medium hover:bg-stone-600 hover:text-white transition-all duration-300 ${isNavLinkActive('/index.html') ? 'bg-stone-600 text-white' : 'text-white'}`}
              >
                Home
              </a>

              {categories.slice(0, getVisibleCategoriesCount()).map((category) => {
                const categoryPath = `/category.html?category=${category.slug}`;
                return (
                  <a
                    key={category.id}
                    href={categoryPath}
                    className={getNavLinkClass(category.slug)}
                  >
                    {getCategoryShortName(category.name)}
                  </a>
                );
              })}

              <a 
                href="/about.html" 
                className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs sm:text-sm font-medium hover:bg-stone-600 hover:text-white transition-all duration-300 ${isNavLinkActive('/about.html') ? 'bg-stone-600 text-white' : 'text-white'}`}
              >
                About Us
              </a>
            </nav>

            {/* Search Area (Icon & Input) */}
            <div ref={searchContainerRef} className={`flex items-center ${windowWidth < BREAKPOINTS.md ? 'flex-grow justify-end' : 'relative ml-2 flex-shrink-0'}`}>
                {/* Search Input (Animated) */}
                <AnimatePresence>
                  {searchInputVisible && (
                    <motion.div
                      key="search-input"
                      initial={{ width: 0, opacity: 0 }}
                      animate={{
                        width: windowWidth < BREAKPOINTS.md ? 
                          (windowWidth < 400 ? 'calc(100% - 100px)' : 'calc(100% - 88px)') : 
                          (windowWidth < BREAKPOINTS.lg ? '100px' : '180px'),
                        opacity: 1
                      }}
                      exit={{ width: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`overflow-hidden ${windowWidth < BREAKPOINTS.md ? 'absolute left-3 top-1/2 -translate-y-1/2' : 'relative flex-shrink-0'}`}
                      style={{ 
                        right: windowWidth < BREAKPOINTS.md ? 
                          (windowWidth < 400 ? 
                            (shouldShowHamburgerMenu() ? '96px' : '60px') : 
                            (shouldShowHamburgerMenu() ? '88px' : '52px')
                          ) : 'auto' 
                      }}
                    >
                      <form onSubmit={handleSearchSubmit} className="relative">
                        <input
                          ref={searchInputRef}
                          type="search"
                          placeholder="Search travel articles..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full h-9 md:h-10 pl-4 pr-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-full focus:ring-2 focus:ring-slate-500 focus:border-transparent outline-none shadow-sm appearance-none"
                          style={{ 
                            WebkitAppearance: 'none', // Attempt to remove default Safari styling
                            MozAppearance: 'textfield',  // Attempt to remove default Firefox styling for search
                            // Add this style to hide the default clear button in WebKit browsers
                            '::-webkit-search-cancel-button': { display: 'none' } 
                          }}
                        />
                        {searchQuery && (
                          <button 
                            type="button" 
                            onClick={() => setSearchQuery('')} 
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white"
                            aria-label="Clear search"
                          >
                            <FaTimes className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Search Toggle Button (Ensured visibility and correct positioning) */}
                <button 
                    ref={searchButtonRef} 
                    onClick={toggleSearchInput} 
                    className={`flex-shrink-0 p-2 rounded-full hover:bg-stone-600 transition-colors z-20 ${
                        searchInputVisible && windowWidth < BREAKPOINTS.md 
                        ? `absolute top-1/2 -translate-y-1/2` 
                        : 'relative ml-1'
                    }`}
                    style={
                      searchInputVisible && windowWidth < BREAKPOINTS.md 
                        ? { 
                            right: windowWidth < 400 ? 
                              (shouldShowHamburgerMenu() ? '52px' : '16px') : 
                              (shouldShowHamburgerMenu() ? '44px' : '8px')
                          }
                        : {}
                    }
                    aria-label={searchInputVisible ? "Close search" : "Open search"}
                >
                  {searchInputVisible ? <FaTimes className="w-4 h-4 text-white" /> : <FaSearch className="w-4 h-4 text-white" />}
                </button>
            </div>

            {/* Mobile/Tablet Menu Toggle - Show when navigation is collapsed */}
            {shouldShowHamburgerMenu() && (
              <div className={`${windowWidth < 400 ? 'ml-1' : 'ml-2'}`}>
                <button 
                  onClick={toggleMobileMenu} 
                  className={`flex-shrink-0 p-2 rounded-full hover:bg-stone-600 transition-colors ${searchInputVisible ? 'hidden' : 'block'}`}
                  aria-label="Toggle menu"
                  style={windowWidth < 400 ? { marginRight: '4px' } : {}}
                >
                  <FaBars className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Menu (Animated) - Show when hamburger is visible */}
      <AnimatePresence>
        {mobileMenuOpen && shouldShowHamburgerMenu() && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-stone-800 absolute top-full left-0 right-0 shadow-lg overflow-hidden z-40"
          >
            <nav className="px-3 pt-3 pb-4 space-y-2">
              <a 
                href="/index.html" 
                className={`block px-3 py-2 rounded-md text-base font-medium hover:bg-stone-700 transition-all duration-300 ${isNavLinkActive('/index.html') ? 'bg-stone-700 text-white' : 'text-white'}`}
                onClick={toggleMobileMenu}
              >
                Home
              </a>

              {categories.map((category) => {
                const categoryPath = `/category.html?category=${category.slug}`;
                return (
                  <a
                    key={category.id}
                    href={categoryPath}
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-stone-700 text-white transition-all duration-300"
                    onClick={toggleMobileMenu}
                  >
                    {getCategoryShortName(category.name)}
                  </a>
                );
              })}

              <a 
                href="/about.html" 
                className={`block px-3 py-2 rounded-md text-base font-medium hover:bg-stone-700 transition-all duration-300 ${isNavLinkActive('/about.html') ? 'bg-stone-700 text-white' : 'text-white'}`}
                onClick={toggleMobileMenu}
              >
                About Us
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;